export const MODAL_NAMES = {
  LIVE_SHOPPING_OVERLAY: 'liveShoppingOverlay',
} as const

/**
 * Which of the two videos (and its curated product list) a page gets.
 * Resolved from the route in useIsLiveShoppingEnabled.ts: iPad-related
 * pages → the EN iPads video, MacBook-related pages and Home → the EN
 * MacBooks video (per the 2-video plan: "iPad video on iPad pages, MacBook
 * video on MacBook pages + homepage"). The CloudBees flags stay
 * page-type-scoped (Home/PlpSearch/Pdp) — video choice is content
 * routing, not rollout control.
 */
export type LiveShoppingCategory = 'ipad' | 'macbook'

/** Grade IDs as used in backbox objectIDs. */
export type LiveShoppingGrade = 9 | 10 | 11 | 12

/** All grade IDs, as used in backbox objectIDs (`<productId>_<gradeId>_<offerType>`). */
export const LIVE_SHOPPING_GRADES: LiveShoppingGrade[] = [9, 10, 11, 12] // 9 = Premium, 10 = Excellent, 11 = Good, 12 = Fair

export type LiveShoppingFeaturedProduct = {
  /**
   * The backbox variant UUID (the `productId` part of its objectIDs). The
   * fetch queries this UUID in every grade, prefers `preferredGrade`, and
   * falls back to the cheapest in-stock grade (Janos's rule,
   * 2026-10-20) — a product only drops out if the whole UUID has no stock.
   */
  productUuid: string
  /** The grade demonstrated in the video — preferred when in stock. */
  preferredGrade: LiveShoppingGrade
  /** Seconds into the video where this product is featured. */
  timestampSeconds: number
}

export type LiveShoppingContent = {
  videoId: string
  products: LiveShoppingFeaturedProduct[]
}

// Production content — activated 2026-10-20 (Janos's go, for devtools
// verification; revert the `true ||` override in default.vue before any
// commit). Source: the "Brief to Studio - Live shopping video" doc's
// Timestamps block (fetched 2026-10-19), verified against both final-cut
// transcripts; grades per that block override the script sections and the
// "Device list | Live shopping video shoot - Q3 2026" Google Sheet.
// Timestamps = the product's "A closer look" segment, else its dedicated
// who-is-it-for segment — all confirmed against the actual audio.
// ⚠️ Product UUIDs are NOT market-agnostic (same specs can be a different
// variant UUID per market) — expanding to another market means re-resolving
// the whole list against that market's index, not just flipping flags.
// The objectID format is `<productId>_<gradeId>_<offerType>`; the fetch
// enumerates offerType 0 (refurb) in all 4 grades per UUID and applies the
// grade-fallback rule at mapping time. Keyboard layouts are curated to the
// market default (QWERTY-English for IE) — see SR-5954_QA_PLAN.md for the
// verification ritual; run it before launch and ~weekly during the pilot.
export const LIVE_SHOPPING_CONTENT: Record<
  LiveShoppingCategory,
  LiveShoppingContent
> = {
  ipad: {
    // "How to shop for refurbished iPads | Back Market", Back Market UK
    // channel (François, Slack 2026-10-20 — final IDs, replacing the ones
    // he first uploaded to the FR channel by mistake; verified via oEmbed)
    videoId: 'hbZwZtDaIA8',
    products: [
      // iPad 11" (2025) A16 128GB Wi-Fi Blue — Premium — product intro @ 00:22
      // (color correction 2026-10-20: the device list said Silver; this
      // UUID is Blue)
      // (also the Premium-grade demo device in the video's grade quiz @ 06:12)
      // https://www.backmarket.ie/en-ie/p/ipad-11-2025/a19ee603-ae1c-4646-9faf-ad61857c202c
      {
        productUuid: 'a19ee603-ae1c-4646-9faf-ad61857c202c',
        preferredGrade: 9,
        timestampSeconds: 22,
      },
      // iPad 10.2" (2021, 9th gen) 64GB Wi-Fi — Fair (doc: Fair, was Good) — product intro @ 08:40
      // ✅ verified on prod 2026-10-20 (widget-identical OR-filter query, €180).
      // Color correction: this UUID is the Space Gray variant — the device
      // list said Silver.
      // https://www.backmarket.ie/en-ie/p/ipad-102-9th-gen-2021-64gb-space-gray-wifi/5e00cb64-a460-4975-a515-5fa9585c9b08?l=12
      {
        productUuid: '5e00cb64-a460-4975-a515-5fa9585c9b08',
        preferredGrade: 12,
        timestampSeconds: 520,
      },
      // iPad Air 11" (2025, 7th gen, M3) 128GB Wi-Fi Purple — Excellent — product intro @ 09:52
      // (color per the 2026-10-20 health-check title)
      // https://www.backmarket.ie/en-ie/p/ipad-air-m3-11-2025/465e4595-10c1-4f71-8d32-eb6da85b793a
      {
        productUuid: '465e4595-10c1-4f71-8d32-eb6da85b793a',
        preferredGrade: 10,
        timestampSeconds: 592,
      },
      // iPad Pro 11" (2024, M4) 256GB Wi-Fi Space Black — Fair — product intro @ 07:58
      // https://www.backmarket.ie/en-ie/p/ipad-pro-11-m4-2024/31a33672-e45a-49ba-baa2-1fe9a3dce9cb
      {
        productUuid: '31a33672-e45a-49ba-baa2-1fe9a3dce9cb',
        preferredGrade: 12,
        timestampSeconds: 478,
      },
      // iPad mini 8.3" (2024, 7th gen) A17 Pro 128GB Wi-Fi Blue — Fair — product intro @ 10:27
      // (also the Fair-grade demo device in the video's grade quiz)
      // https://www.backmarket.ie/en-ie/p/ipad-mini-2024/193bee1c-a5ef-4fef-9fc8-d55b40db16a5
      {
        productUuid: '193bee1c-a5ef-4fef-9fc8-d55b40db16a5',
        preferredGrade: 12,
        timestampSeconds: 627,
      },
    ],
  },
  macbook: {
    // "How to shop for refurbished MacBooks | Back Market", Back Market UK
    // channel (François, Slack 2026-10-20 — final IDs, replacing the ones
    // he first uploaded to the FR channel by mistake; verified via oEmbed)
    videoId: 'MNPOko8hAiM',
    products: [
      // MacBook Air 15-inch (2025) M4 10-core, 16GB RAM, 256GB SSD — doc says Good — product intro @ 07:51
      // ✅ swapped 2026-10-20: the original variant (`f2b0ba48…`, from the
      // device list) went FULLY out of stock in IE — all 4 grades verified
      // dead (it was in stock as Excellent back in July) — live proof of the
      // stock-drift risk. The doc's Good grade has no IE stock on any
      // surfaced variant either, so we feature the in-stock
      // market-default-layout 15" M4: QWERTY-English, €1350. Curated as
      // preferredGrade Good (the doc's grade) — the fetch will currently
      // fall back to the in-stock Excellent (€1350, cheapest available); a
      // Good restock automatically wins from then on.
      // https://www.backmarket.ie/en-ie/p/macbook-air-15-inch-2025-m4-10-core-and-10-core-gpu-16gb-ram-ssd-256gb-qwerty-english/16bb2a78-4809-4e56-bf59-97b8194b4b17?l=10
      {
        productUuid: '16bb2a78-4809-4e56-bf59-97b8194b4b17',
        preferredGrade: 11,
        timestampSeconds: 471,
      },
      // MacBook Air 13-inch (2020) M1 8-core/7-core, 8GB RAM, 128GB SSD — Excellent (doc: Excellent, was Good) — product intro @ 00:21; grade confirmed in the quiz @ 03:05
      // (also the Excellent-grade demo device in the video's grade quiz — the
      // Excellent grade is audio-confirmed: "this one I would say is probably
      // excellent... Yeah, that's right")
      // ✅ swapped to a QWERTY-English variant 2026-10-20 — enumeration with
      // `distinct: false` + an English-token probe found English-layout M1
      // Airs in IE after all (Yves was right; the earlier "no English M1 Air
      // in IE" call from deduped discovery was wrong — relevance ranking had
      // them below the top 100). Same specs + grade as the video device:
      // Excellent, €653. Fair/Good/Excellent all in stock on this UUID, so
      // the runtime grade fallback has room. Anto independently shared this
      // exact product page in Slack the same day.
      // https://www.backmarket.ie/en-ie/p/macbook-air-2020-13-inch-m1-8-core-and-7-core-gpu-8gb-ram-ssd-128gb-qwerty/00e60dbb-24fc-4061-9d6f-228410f5a22e?l=10
      {
        productUuid: '00e60dbb-24fc-4061-9d6f-228410f5a22e',
        preferredGrade: 10,
        timestampSeconds: 21,
      },
      // MacBook Pro 13-inch (2022) M2 8-core/10-core, 8GB RAM, 256GB SSD — Fair — product intro @ 06:34
      // ✅ swapped to a QWERTY-English variant 2026-10-20 (the previous
      // UUID was AZERTY-French — the only representative deduped
      // discovery surfaced; the `distinct: false` enumeration found
      // English ones). Fair €699, which is also the cheapest of this
      // UUID's in-stock grades (Excellent €934, Good €943) — preferred
      // = cheapest here. Storage correction vs the device list (512GB):
      // the video never states storage.
      // https://www.backmarket.ie/en-ie/p/macbook-pro-13-inch-2022-m2-8-core-and-10-core-gpu-8gb-ram-ssd-256gb-qwerty/ef20e8dd-bcbf-4d94-8933-15f59560b9b9?l=12
      {
        productUuid: 'ef20e8dd-bcbf-4d94-8933-15f59560b9b9',
        preferredGrade: 12,
        timestampSeconds: 394,
      },
      // MacBook Pro 14" (2023, M3 Pro 11-core, 18GB RAM, 512GB SSD) — Fair —
      // product intro @ 07:11; also the Fair-grade demo device in the
      // video's grade quiz @ 02:02. Confirmed against the final cut (07:12): "This
      // one is a MacBook Pro. I believe it's an M3 Pro device" + HDMI &
      // SD-card reader ports — the 13" Pro never had those, and the video's
      // recap names exactly four MacBooks (M1 Air, M2 Pro, M3 Pro, M4 Air),
      // so the MacBook Pro 13" (2020, M1) previously in this slot is NOT in
      // the video at all.
      // ✅ swapped to a QWERTY-English variant 2026-10-20 (was QWERTY-Italian
      // — the only representative the deduped discovery surfaced): same
      // specs as the video device (M3 Pro 11-core, 18GB, 512GB) in
      // QWERTY-English, Fair €1611 — market-default layout rule satisfied
      // with the video's grade intact. Good €1620 also in stock.
      // https://www.backmarket.ie/en-ie/p/macbook-pro-142-inch-2023-m3-pro-11-core-and-14-core-gpu-18gb-ram-ssd-512gb-qwerty-english/946234a9-c96f-417e-a926-51e88c732b47?l=12
      {
        productUuid: '946234a9-c96f-417e-a926-51e88c732b47',
        preferredGrade: 12,
        timestampSeconds: 431,
      },
      // MacBook Neo 13" (2026, A18 Pro, 8GB RAM, 256GB SSD) — Excellent —
      // feature comparison @ 09:45 (also discussed @ 10:13, "Neo or
      // refurbished for first-time buyers/students"). Tagged per team
      // decision (Eugena + Yves, Slack 2026-10-20: customers have it top of
      // mind; out 6+ months; since the June price hike our price point is an
      // opportunity).
      // ✅ resolved + verified on prod 2026-10-20: €739 — IE supply
      // confirmed (Yves had cited FR+UK only). Grade not stated in the
      // video/doc; this is the in-stock IE listing (Excellent,
      // QWERTY-English).
      // https://www.backmarket.ie/en-ie/p/macbook-neo-13-inch-2026-a18-pro-6-core-and-5-core-gpu-8gb-ram-ssd-256gb-qwerty-english/dadad16f-2f17-4356-ba70-84547a4316a4?l=10
      {
        productUuid: 'dadad16f-2f17-4356-ba70-84547a4316a4',
        preferredGrade: 10,
        timestampSeconds: 585,
      },
    ],
  },
}

// ── Previous local-dev demo content (TEMP) ────────────────────────────────
// The preprod objectID list that was active while the production content
// was being resolved — production objectIDs don't resolve against the
// local preprod Algolia index, so local dev with the production list
// above will show an empty product list (the empty state itself is a QA
// case; see SR-5954_QA_PLAN.md). Kept here for reverting local demos:
//
// ipad/macbook: videoId 'sqDeAt4M29U' (PLACEHOLDER — a random BM iPad
// video, NOT the final cut) with these 6 preprod demo entries:
// ef5660d2-6883-4b81-b47d-86e5720687ef (iPhone 13, grade 10, ts 10 —
// flash sales + seller flash sales deals), 8d1132f5-4a5e-4151-9024-
// c67eb9d38f86 (Apple Watch S9, grade 10, ts 25 — flash sales deal),
// b20f8617-77b2-4cbd-b8cf-87ad447db21e (iPad 10.9" 2022, grade 10, ts 40
// — no deal), d173b337-9318-4b5f-a885-20ec1d227fcf (iPhone 16, grade 12,
// ts 55), 3ec08205-d3ed-45c3-b50d-2d4e192b059e (MacBook Air M2 13", grade
// 11, ts 70), 11993358-183d-477b-a7ba-6606830ccc5f (Galaxy Tab A9, grade
// 10, ts 85).
